import { useEffect, useRef } from "react";
import { string, bool, number, object, func, node } from "prop-types";
import Headline from "../Headline/component";
import { Label } from "../../../Label/component";
import Description from "../../../Description/component";
import LinkWrap from "../../../LinkWrap/component";
import Metadata from "../Metadata/component";
import CardImageWrap from "../CardImageWrap/component";
import { useImageDerivatives } from "../../../../hooks/useImageDerivatives";
import { usePreviewBase } from "../../../../hooks/usePreviewBase";
import { useNormalizeClass } from "../../../../hooks/useNormalizeClass";
import classNames from "classnames";
import { createLinkProps } from "../../../../utils/dataTransform/utils";
import { DEFAULT_IMAGE_SIZES } from "./constants";

/**
 * Function representing the CardFeatured component for the lineup optimization test.
 *
 *
 */
export const CardFeaturedReversed = ({
    external,
    sourceId,
    title,
    url,
    id,
    className,
    displayComments,
    focus = false,
    media,
    imageURL,
    description,
    descriptionId,
    flag,
    flagId,
    baseSection = "",
    subSection = "",
    updateTime,
    departments,
    metadataId,
    cardClickHandler,
    videoCardOverlayType,
    videoDuration,
    videoAirDate,
    itemType,
    path,
    showTimestamp = true,
    videoStreamType,
}) => {
    const domNode = useRef(null);

    useEffect(() => {
        // assign focus based on props
        if (focus) {
            domNode.current.focus();
        }
    }, [focus]);

    const flagClassName = useNormalizeClass("flag", flag);
    const classes = classNames(
        "card",
        "cardFeatured",
        "cardFeaturedReversed",
        flagClassName,
        {
            hasVideo: media,
        },
        className
    );
    const labelProps = {
        flag: flag,
        external: external,
        id: flagId,
    };
    const linkProps = createLinkProps({
        sourceId,
        external,
        url,
        cardClickHandler,
    });
    const metaProps = {
        baseSection: baseSection,
        subSection: subSection,
        updated: updateTime,
        showTimestamp: showTimestamp,
        departments: departments,
        id: metadataId,
        url: url,
        displayComments: displayComments,
        path: path,
    };

    const imageUrl = usePreviewBase(imageURL);

    const [srcSet] = useImageDerivatives(imageUrl);

    const imageProps = {
        srcSet,
        sizes: DEFAULT_IMAGE_SIZES,
        url: imageUrl,
        loading: null,
        isAboveFold: true,
    };

    const videoLinkImageOverlayProps = {
        videoCardOverlayType,
        videoDuration,
        videoAirDate,
        videoStreamType,
    };

    return (
        <div id={id} className={classes} data-testid="card-featured">
            <LinkWrap
                className="contentWrapper"
                data-cy={`type-${itemType}`}
                {...linkProps}
            >
                <div ref={domNode} className="card-content">
                    <div className="card-content-top">
                        <Label {...labelProps} />
                        <Headline text={title} />
                        {description && (
                            <Description
                                id={descriptionId}
                                text={description}
                            />
                        )}
                    </div>
                    <div className="card-content-bottom">
                        <Metadata {...metaProps} />
                    </div>
                </div>
            </LinkWrap>
            {media ? (
                media
            ) : (
                <LinkWrap
                    className="mediaItem"
                    {...linkProps}
                    tabIndex="-1"
                    aria-hidden={true}
                >
                    <CardImageWrap
                        {...{ imageProps, videoLinkImageOverlayProps }}
                    />
                </LinkWrap>
            )}
        </div>
    );
};

CardFeaturedReversed.propTypes = {
    // Is the URL external (in which case we don't handle it)?
    external: bool.isRequired,
    // Item ID from original source, e.g. CMS.
    sourceId: string.isRequired,
    // The headline to display.
    title: string.isRequired,
    // The URL that the card links to
    url: string.isRequired,
    // ID of card
    id: string.isRequired,

    // OPTIONAL
    className: string,
    // Display comments for this card
    displayComments: bool,

    focus: bool,
    media: node,
    imageURL: string,
    description: string,
    descriptionId: string,
    flag: string,
    flagId: string,
    baseSection: string,
    subSection: string,
    updateTime: number,
    departments: object,
    metadataId: string,
    cardClickHandler: func,
    itemType: string,
    showTimestamp: bool,
};

export default CardFeaturedReversed;
